// Action (step 4): three cards, Allow · Require MFA · Deny, the same on every
// product, each with its colour, icon and a few words; the product's
// sentence under them. The product's extra (AD's MFA prompt) sits under it;
// Deny for Everyone without fallback warns.
import React from "react";

import { AN_COLORS, AN_FONT } from "../../../design-system/tokens";
import { POLICY_ACTIONS, actionSentence, broadDenyWarning } from "../../../components/policyShell/signals";
import { Note, StepCard, actionMeta } from "../ui";

export const showsBroadDenyWarning = (adapter, draft) =>
  !!(adapter.features?.fallback && draft.action === "block" && adapter.who?.everyone && draft.who?.everyone && !draft.fallback);

const BLURB = {
  allow: "Let it through, no extra step.",
  mfa_required: "Ask for a second factor first.",
  block: "Stop it, whatever else matches.",
};

function ActionCards({ value, onChange }) {
  const pick = (i) => onChange(POLICY_ACTIONS[(i + POLICY_ACTIONS.length) % POLICY_ACTIONS.length].value);
  return (
    <div role="radiogroup" aria-label="Action" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(10.5rem, 1fr))", gap: "0.75rem" }}>
      {POLICY_ACTIONS.map((a, i) => {
        const on = a.value === value;
        const m = actionMeta(a.value);
        const Icon = m.icon;
        return (
          <button
            key={a.value}
            type="button"
            role="radio"
            aria-checked={on}
            aria-label={a.label}
            tabIndex={on ? 0 : -1}
            onClick={() => onChange(a.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowRight" || e.key === "ArrowDown") {
                e.preventDefault();
                pick(i + 1);
              } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
                e.preventDefault();
                pick(i - 1);
              }
            }}
            style={{
              display: "grid",
              gap: "0.5rem",
              textAlign: "left",
              padding: "0.875rem 1rem",
              borderRadius: "0.625rem",
              border: `${on ? 2 : 1}px solid ${on ? m.solid : AN_COLORS.line}`,
              margin: on ? 0 : "1px",
              background: on ? m.bg : AN_COLORS.surface,
              cursor: "pointer",
              fontFamily: AN_FONT,
              transition: "border-color 120ms ease, background 120ms ease",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <span
                aria-hidden
                style={{
                  width: "1.75rem",
                  height: "1.75rem",
                  borderRadius: "0.5rem",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: on ? m.solid : m.bg,
                  color: on ? "#fff" : m.fg,
                }}
              >
                <Icon size={16} />
              </span>
              <span style={{ fontSize: "0.9375rem", fontWeight: 650, color: on ? m.fg : AN_COLORS.ink }}>{a.label}</span>
            </span>
            <span aria-hidden style={{ fontSize: "0.8125rem", color: AN_COLORS.muted, lineHeight: 1.45 }}>
              {BLURB[a.value]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function ActionSection({ adapter, draft, patchDraft, sectionProps }) {
  const subject = adapter.labels?.subject || "sign-in";
  const sentence = adapter.features?.actionSentences?.[draft.action] || actionSentence(draft.action, subject);
  const Extra = adapter.features?.ActionExtra;
  return (
    <StepCard id="policy-section-action" step={4} title="What should happen?" help={`When a ${subject} matches this policy.`} done={!!draft.action}>
      <ActionCards value={draft.action} onChange={(v) => patchDraft({ action: v })} />
      <div style={{ fontSize: "0.8125rem", color: AN_COLORS.ink3, lineHeight: 1.5 }}>{sentence}</div>
      {Extra && <Extra {...sectionProps} action={draft.action} />}
      {showsBroadDenyWarning(adapter, draft) && <Note tone="warn">{broadDenyWarning(subject)}</Note>}
    </StepCard>
  );
}
